// =========================================================================
// Progreso · antropometría · lógica pura
//
// Sin React y sin red: se puede probar entera. El reloj se inyecta siempre,
// para que las pruebas no dependan del día en que se ejecuten.
//
// Regla de este módulo: describe, no valora. Devuelve cuánto se ha movido una
// medida y en qué dirección. NO dice si eso es bueno, no clasifica a nadie y
// no calcula categorías (un "sobrepeso" es un juicio clínico, y este módulo
// es material educativo).
// =========================================================================
import type { Cambio, MedidaCorporal, PuntoSerie } from './tipos';

/**
 * Ruido propio de cada medida, por debajo del cual un cambio no significa
 * nada. Una báscula doméstica se mueve sola varios hectogramos según la hora
 * y la hidratación; una cinta métrica depende de dónde se coloque.
 * Llamar "progreso" a ese ruido es fabricar una señal que no existe.
 */
export const RUIDO_PESO_KG = 0.5;
export const RUIDO_CINTURA_CM = 1.0;

/** Ordena por fecha ascendente y deja UNA medida por día: la última escrita. */
export function normalizar(medidas: MedidaCorporal[]): MedidaCorporal[] {
  const porDia = new Map<string, MedidaCorporal>();
  for (const m of medidas) {
    const dia = m.fecha.slice(0, 10);
    const previa = porDia.get(dia);
    // Se fusiona: si un día se pesó por la mañana y se midió la cintura por
    // la tarde, son la misma medida del día, no dos que se pisan.
    porDia.set(dia, {
      fecha: dia,
      pesoKg: m.pesoKg ?? previa?.pesoKg,
      cinturaCm: m.cinturaCm ?? previa?.cinturaCm,
    });
  }
  return [...porDia.values()].sort((a, b) => a.fecha.localeCompare(b.fecha));
}

function etiquetar(fecha: string): string {
  const [, mes, dia] = fecha.split('-');
  return `${dia}/${mes}`;
}

/**
 * Serie de una medida, lista para dibujar. Los días sin ese dato NO aparecen:
 * no se interpola ni se rellena con ceros. Una línea inventada entre dos
 * puntos reales es una afirmación que la persona nunca hizo.
 */
export function serie(
  medidas: MedidaCorporal[],
  campo: 'pesoKg' | 'cinturaCm',
): PuntoSerie[] {
  return normalizar(medidas)
    .filter((m) => typeof m[campo] === 'number')
    .map((m) => ({
      fecha: m.fecha,
      valor: m[campo] as number,
      etiqueta: etiquetar(m.fecha),
    }));
}

/**
 * Cuánto se ha movido una medida entre el primer y el último registro.
 * Devuelve null con menos de dos registros: con un punto no hay progresión
 * que enseñar, y fingirla sería el mismo error que el cero por ausencia.
 */
export function cambio(
  medidas: MedidaCorporal[],
  campo: 'pesoKg' | 'cinturaCm',
): Cambio | null {
  const puntos = serie(medidas, campo);
  if (puntos.length < 2) return null;

  const desde = puntos[0].valor;
  const hasta = puntos[puntos.length - 1].valor;
  const diferencia = +(hasta - desde).toFixed(1);
  const ruido = campo === 'pesoKg' ? RUIDO_PESO_KG : RUIDO_CINTURA_CM;

  return {
    desde,
    hasta,
    diferencia,
    direccion:
      Math.abs(diferencia) < ruido ? 'estable' : diferencia < 0 ? 'baja' : 'sube',
    registros: puntos.length,
  };
}

/**
 * Índice de masa corporal, redondeado a un decimal. Es aritmética sobre datos
 * declarados, y por eso se puede calcular. Se devuelve el NÚMERO Y NADA MÁS:
 * la clasificación en categorías sí sería un juicio clínico y no pertenece a
 * un producto educativo.
 */
export function imc(pesoKg: number, tallaCm: number): number | null {
  if (!(pesoKg > 0) || !(tallaCm > 0)) return null;
  const m = tallaCm / 100;
  return Math.round((pesoKg / (m * m)) * 10) / 10;
}

/**
 * Une el histórico de peso que sirve el servidor con las medidas declaradas en
 * este dispositivo.
 *
 * Hace falta porque son dos orígenes distintos y ninguno basta solo: el
 * servidor no conoce la cintura, y el registro local no existe para quien
 * lleva meses usando la app.
 *
 * El histórico del servidor solo RELLENA LO ANTERIOR AL PRIMER PESO PROPIO.
 * Fusionarlos día a día parecía lo correcto y en pantalla dibujaba un
 * serrucho: los dos orígenes traen días distintos con valores distintos, así
 * que la curva subía y bajaba alternando fuentes en vez de mostrar una
 * evolución. Desde que la persona empieza a pesarse, lo que ella anota ES el
 * registro de peso.
 *
 * El corte se hace por el primer PESO propio y no por el primer registro: si
 * alguien anota solo la cintura, ese día se quedaría sin su peso.
 */
export function fusionarHistorico(
  historicoPeso: { date: string; weight: number }[],
  locales: MedidaCorporal[],
): MedidaCorporal[] {
  const propias = normalizar(locales);
  const primerPesoPropio = propias.find((p) => p.pesoKg != null)?.fecha;

  const delServidor = historicoPeso
    .filter((h) => Number.isFinite(h.weight) && h.weight > 0)
    .map((h) => ({ fecha: h.date.slice(0, 10), pesoKg: h.weight }))
    .filter((h) => primerPesoPropio === undefined || h.fecha < primerPesoPropio);

  return normalizar([...delServidor, ...propias]);
}

/**
 * Fechas comunes a las dos series, en orden. Es lo que permite dibujar los dos
 * paneles con el MISMO eje de tiempo: sin un eje compartido, dos curvas una
 * encima de otra no se pueden comparar aunque lo parezcan.
 */
export function ejeComun(medidas: MedidaCorporal[]): string[] {
  return normalizar(medidas)
    .filter((m) => m.pesoKg != null || m.cinturaCm != null)
    .map((m) => m.fecha);
}
